import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IntegratorSelectComponent } from '@wheere-front/shared-ui';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { toSignal } from '@angular/core/rxjs-interop';
import { SitesV7HttpClient } from '../data/sites-v7.service';
import { SitePreset } from '../data/site.model';
import { fromIntegratorSitePreset } from '../data/integrator-views';

interface PickerOption {
  value: string;
  label: string;
  isDefault: boolean;
  comment?: string;
  preset: SitePreset;
}

/**
 * V8 preset picker. Loads the per-org preset catalog (siteType-agnostic since V8.1), surfaces
 * presets in a PrimeNG select with the default flagged, and emits the picked preset (full
 * payload, not just id) so the caller can both POST the id to the layout endpoint AND drive
 * the {@code SiteCoverageCheckComponent} with its precomputed coverage.
 */
@Component({
  selector: 'lib-site-preset-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, IntegratorSelectComponent, TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-2">
      <label class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400">
        {{ 'sitePresetPicker.label' | transloco }}
      </label>
      <!-- The "(default)" suffix on the option label tells the operator which
           preset is the org-wide default — same intent as the previous
           {@code <p-tag>DEFAULT</p-tag>} but without the extra widget per
           row. The label is computed in {@link options}. -->
      <lib-integrator-select
        class="block"
        width="100%"
        size="md"
        accent="primary"
        [placeholder]="'sitePresetPicker.placeholder' | transloco"
        [filterable]="true"
        [disabled]="disabled()"
        [options]="options()"
        [ngModel]="selectedPresetId()"
        (ngModelChange)="onChange($event)" />

      @if (loading()) {
        <p class="text-xs text-gray-500 dark:text-slate-400">{{ 'sitePresetPicker.loading' | transloco }}</p>
      } @else if (options().length === 0 && !!organizationId()) {
        <p class="text-warmYellow-700 dark:text-warmYellow-400 text-xs">
          {{ 'sitePresetPicker.empty' | transloco }}
        </p>
      } @else if (selectedPreset()?.comment) {
        <p class="text-xs italic text-gray-600 dark:text-slate-400">{{ selectedPreset()?.comment }}</p>
      }
    </div>
  `,
})
export class SitePresetPickerComponent {
  organizationId = input<string | null>(null);

  /** When true, fetch from the IP-safe `/integrator/site-presets` endpoint (no raw freq/calib/
   * theta blobs). The integrator app sets this; wheere-inside's admin catalog leaves it false to
   * keep the full payload its CRUD inputs need. */
  integratorScope = input<boolean>(false);

  /**
   * Pre-select the org default preset when nothing is selected (default `true`, inside's
   * behaviour). The integrator turns it off on a site whose custom technical files must be kept:
   * a silent default would overwrite them at the next layout save.
   */
  autoSelectDefault = input<boolean>(true);

  /** Lecture seule : l'utilisateur voit le preset mais ne peut pas en changer. */
  disabled = input<boolean>(false);

  /** Pre-selected preset id (e.g. site's currently active preset). Two-way bindable. */
  selectedPresetId = model<string | null>(null);

  /** Full preset payload of the current selection — drives the coverage check + display. */
  selected = output<SitePreset | null>();

  protected readonly presets = signal<SitePreset[]>([]);
  protected readonly loading = signal(false);

  protected readonly options = computed<PickerOption[]>(() => {
    // Touch the active lang signal so the option labels re-evaluate on language change.
    this.activeLang();
    const defaultSuffix = this.#transloco.translate('sitePresetPicker.defaultSuffix');
    // ADR-039 : « Nom — jusqu'à N antennes (défaut) », N = min(freq_delta, colonnes calib).
    const upTo = (n: number | undefined) =>
      n != null ? ` — ${this.#transloco.translate('sitePresetPicker.upToAntennas', { count: n })}` : '';
    return this.presets().map(p => ({
      value: p.id,
      label: p.name + upTo(p.maxAntennas) + (p.isDefault ? defaultSuffix : ''),
      isDefault: !!p.isDefault,
      comment: p.comment,
      preset: p,
    }));
  });

  protected readonly selectedPreset = computed<SitePreset | null>(() => {
    const id = this.selectedPresetId();
    if (!id) return null;
    return this.presets().find(p => p.id === id) ?? null;
  });

  #api = inject(SitesV7HttpClient);
  #transloco = inject(TranslocoService);
  protected readonly activeLang = toSignal(this.#transloco.langChanges$, {
    initialValue: this.#transloco.getActiveLang(),
  });

  /** Re-load the catalog whenever the org changes. */
  #lastOrg: string | null = null;
  constructor() {
    effect(
      () => {
        const orgId = this.organizationId();
        if (orgId === this.#lastOrg) return;
        this.#lastOrg = orgId;
        void this.load(orgId);
      },
      { allowSignalWrites: true }
    );

    // Re-emit selected preset payload whenever the id or catalog changes.
    effect(
      () => {
        const p = this.selectedPreset();
        this.selected.emit(p);
      },
      { allowSignalWrites: true }
    );
  }

  protected onChange(presetId: string | null): void {
    this.selectedPresetId.set(presetId);
  }

  private async load(orgId: string | null): Promise<void> {
    if (!orgId) {
      this.presets.set([]);
      return;
    }
    this.loading.set(true);
    try {
      // Integrator scope → slim endpoint (no raw freq/calib/theta blobs ever reach the browser),
      // mapped back into the canonical SitePreset shape the picker works with.
      const list = this.integratorScope()
        ? (await this.#api.listIntegratorPresets(orgId)).map(fromIntegratorSitePreset)
        : await this.#api.listPresets(orgId);
      this.presets.set(list);
      // Auto-select the default if nothing's currently selected.
      if (!this.selectedPresetId() && this.autoSelectDefault()) {
        const def = list.find(p => p.isDefault);
        if (def) this.selectedPresetId.set(def.id);
      }
    } catch {
      this.presets.set([]);
    } finally {
      this.loading.set(false);
    }
  }
}
